import { hasWidgetHydrationPayload } from '../../builderCore'
import type { SavedDashboardViewState } from '../../types'
import { areViewStatesEqual, serializeViewState } from '../savedViews'
import type { DashboardViewState, WidgetSessionState } from './state'
import { createWidgetSessionState } from './state'

function stableStringify(value: unknown) {
  return JSON.stringify(value)
}

export function selectDashboardReady(state: DashboardViewState) {
  return state.dashboardLoad.bootstrapStatus === 'ready' && Boolean(state.dashboard)
}

export function selectDraftFilters(state: DashboardViewState) {
  return state.draftFilters
}

export function selectAppliedFilters(state: DashboardViewState) {
  return state.appliedFilters
}

export function selectHasPendingGlobalFilterChanges(state: DashboardViewState) {
  return stableStringify(state.draftFilters) !== stableStringify(state.appliedFilters)
}

// `expected` is the active view as reconciled against this dashboard (see reconcileViewState).
export function selectIsViewModified(state: DashboardViewState, expected: SavedDashboardViewState | null) {
  if (!expected) return false
  return !areViewStatesEqual(serializeViewState(state), expected)
}

export function selectSlicerOptionsByField(state: DashboardViewState) {
  return state.slicerOptions.byField
}

export function selectSlicerLoadingByField(state: DashboardViewState) {
  return state.slicerOptions.loadingByField
}

export function selectHydratableWidgetIds(state: DashboardViewState) {
  return (state.dashboard?.widgets ?? [])
    .filter(hasWidgetHydrationPayload)
    .map((widget) => widget.id)
}

export function selectWidgetSession(state: DashboardViewState, widgetId: string): WidgetSessionState {
  return state.widgets[widgetId]
    ?? createWidgetSessionState(state.dashboard?.widgets.find((widget) => widget.id === widgetId))
}

export function selectWidgetCanPageBackend(state: DashboardViewState, widgetId: string) {
  const session = selectWidgetSession(state, widgetId)
  return session.data.hasPaginationMetadata === true
    && session.data.totalRows > 0
    && session.data.page > 0
    && session.data.pageSize > 0
}

export function selectInsightInvalidationSignature(state: DashboardViewState) {
  if (!state.dashboard) return null

  const appliedFilterKey = stableStringify(state.appliedFilters)
  const parts = state.dashboard.widgets
    .filter(hasWidgetHydrationPayload)
    .map((widget) => {
      const session = selectWidgetSession(state, widget.id)
      return [
        widget.id,
        session.status,
        session.data.lastUpdatedAt,
        session.data.daxQuery,
        session.query.groupByDimension,
        session.query.sortColumn,
        session.query.sortDirection,
        session.query.drilldown
          ? `${session.query.drilldown.field}|${session.query.drilldown.value}|${session.query.drilldown.dimension}`
          : null,
        appliedFilterKey,
      ].join('::')
    })

  return parts.join('||')
}
